/**
 * Warranty Manager JavaScript
 * Handles all warranty management functionality
 */

(function() {
  'use strict';

  // Configuration
  const STORAGE_KEY = 'rod_warranties';
  const EXPIRING_DAYS = 30; // Days before expiration to show as "expiring soon"
  const customerId = window.warrantyManagerConfig?.customerId || null;
  const customerEmail = window.warrantyManagerConfig?.customerEmail || null;
  const customerOrders = window.warrantyManagerConfig?.customerOrders || [];
  
  // Firebase Configuration
  const firebaseConfig = window.warrantyManagerConfig?.firebaseConfig || null;
  let db = null; // Firestore database instance
  
  // Initialize Firebase if config is provided
  if (firebaseConfig && typeof firebase !== 'undefined' && firebase.initializeApp) {
    try {
      firebase.initializeApp(firebaseConfig);
      db = firebase.firestore();
      console.log('Firebase initialized successfully');
    } catch (error) {
      console.error('Firebase initialization error:', error);
    }
  }
  
  // API Endpoint Configuration (legacy, for custom backend)
  const API_ENDPOINT = window.warrantyManagerConfig?.apiEndpoint || null;
  const CLAIMS_ENDPOINT = window.warrantyManagerConfig?.claimsEndpoint || 
    (API_ENDPOINT ? API_ENDPOINT.replace('warranty-endpoint.php', 'claims-endpoint.php') : null);
  const IS_ROUTE_BASED_API = API_ENDPOINT && API_ENDPOINT.includes('/api/warranties') && !API_ENDPOINT.includes('.php');
  const USE_API = customerId !== null && API_ENDPOINT !== null && API_ENDPOINT !== '';
  
  // Use Firebase if configured, otherwise fall back to API or localStorage
  const USE_FIREBASE = db !== null && customerId !== null;

  // Warranty Series Data - Total warranty duration in years
  const WARRANTY_SERIES = {
    'Crappie Slayer': { duration: 1, fullWarranty: 1, limitedWarranty: 0, replacementFee: 25 },
    'Ghost 2': { duration: 1, fullWarranty: 1, limitedWarranty: 0, replacementFee: 25 },
    'Cloak': { duration: 1, fullWarranty: 1, limitedWarranty: 0, replacementFee: 25 },
    'Torch': { duration: 1, fullWarranty: 1, limitedWarranty: 0, replacementFee: 35 },
    'Silhouette': { duration: 1, fullWarranty: 1, limitedWarranty: 0, replacementFee: 35 },
    'Zeus': { duration: 3, fullWarranty: 1, limitedWarranty: 2, replacementFee: 40 },
    'Salt Series': { duration: 3, fullWarranty: 1, limitedWarranty: 2, replacementFee: 40 },
    'Walleye': { duration: 3, fullWarranty: 1, limitedWarranty: 2, replacementFee: 40 },
    'Wheeler Signature 1': { duration: 3, fullWarranty: 1, limitedWarranty: 2, replacementFee: 50 },
    'Wheeler Signature 2': { duration: 3, fullWarranty: 1, limitedWarranty: 2, replacementFee: 50 },
    'Micro Magic Pro': { duration: 5, fullWarranty: 1, limitedWarranty: 4, replacementFee: 60 },
    'Incite': { duration: 5, fullWarranty: 1, limitedWarranty: 4, replacementFee: 60 },
    'Pro Series': { duration: 5, fullWarranty: 1, limitedWarranty: 4, replacementFee: 75 },
    'Wheeler Select': { duration: 5, fullWarranty: 1, limitedWarranty: 4, replacementFee: 75 },
    'Other/Discontinued': { duration: 0, fullWarranty: 0, limitedWarranty: 0, replacementFee: 0 }
  };

  // Warranty series options for dropdown
  const WARRANTY_SERIES_OPTIONS = [
    'Crappie Slayer',
    'Ghost 2',
    'Cloak',
    'Torch',
    'Silhouette',
    'Zeus',
    'Salt Series',
    'Walleye',
    'Wheeler Signature 1',
    'Wheeler Signature 2',
    'Micro Magic Pro',
    'Incite',
    'Pro Series',
    'Wheeler Select',
    'Other/Discontinued'
  ];

  // Known rod product names (all products that are rods)
  const ROD_PRODUCT_NAMES = [
    'glade runner rod',
    'torch series',
    'cloak series',
    'ghost 2',
    'crappie slayer',
    'silhouette series',
    'pro series',
    'zeus series',
    'jacob wheeler 2.0',
    'micro magic pro',
    'jacob wheeler select',
    'salt series',
    'walleye',
    'wheeler signature',
    'incite'
  ];

  /**
   * Check if a product title matches any known rod product
   */
  function isRodProduct(title) {
    if (!title) return false;
    const lowerTitle = title.toLowerCase();
    // Check if title includes "rod" OR matches any known rod product name
    return lowerTitle.includes('rod') || ROD_PRODUCT_NAMES.some(rodName => lowerTitle.includes(rodName));
  }

  // State
  let warranties = [];
  let editingWarrantyId = null;

  // DOM Elements
  let dashboardEl;
  let modalOverlay;

  /**
   * Initialize the warranty manager
   */
  async function init() {
    dashboardEl = document.getElementById('warrantyDashboard');
    
    if (!dashboardEl) {
      return; // Customer not logged in or dashboard not found
    }

    // Show loading state
    dashboardEl.innerHTML = '<div class="loading">Loading warranties...</div>';
    
    console.log('=== WARRANTY MANAGER INIT ===');
    console.log('Customer ID:', customerId);
    console.log('Customer orders count (from Liquid):', customerOrders.length);
    
    // Load warranties from API or localStorage
    await loadWarranties();
    console.log('Warranties loaded from DB:', warranties.length);
    
    // Auto-populate warranties from past orders (always check for new orders)
    const ordersBeforePopulate = warranties.length;
    if (customerOrders.length > 0) {
      await autoPopulateFromOrders();
      
      // Show notification if new warranties were auto-created
      if (warranties.length > ordersBeforePopulate) {
        const count = warranties.length - ordersBeforePopulate;
        showNotification(`✓ ${count} warranty(ies) auto-created from your past orders!`, 'success');
      }
    } else {
      console.warn('⚠️ No customer orders available for auto-population');
      console.warn('This could mean:');
      console.warn('  1. You have no orders in your account');
      console.warn('  2. The order hasn\'t synced yet (wait a few minutes after placing order)');
      console.warn('  3. There\'s an issue with the Liquid template');
    }
    
    renderDashboard();
    setupEventListeners();
    console.log('=== INIT COMPLETE ===');
  }

  /**
   * Setup event listeners
   */
  function setupEventListeners() {
    // Delegate click events for dynamic elements
    document.addEventListener('click', function(e) {
      const target = e.target;
      
      // Add warranty button
      if (target.matches('.btn-add-warranty')) {
        e.preventDefault();
        showAddWarrantyModal();
      }
      
      // Edit warranty button
      if (target.matches('.btn-edit-warranty')) {
        e.preventDefault();
        const warrantyId = target.getAttribute('data-id');
        showEditWarrantyModal(warrantyId);
      }
      
      // Delete warranty button
      if (target.matches('.btn-delete-warranty')) {
        e.preventDefault();
        const warrantyId = target.getAttribute('data-id');
        deleteWarranty(warrantyId);
      }
      
      // Print warranty form button
      if (target.matches('.btn-print-warranty-form')) {
        e.preventDefault();
        const warrantyId = target.getAttribute('data-id');
        printWarrantyForm(warrantyId);
      }
      
      // Start warranty claim button
      if (target.matches('.btn-start-warranty-claim')) {
        e.preventDefault();
        const warrantyId = target.getAttribute('data-id');
        startWarrantyClaim(warrantyId);
      }
      
      // Warranty claim navigation (skip step 3 submit - handled separately)
      if (target.matches('.btn-claim-next') && !target.hasAttribute('data-submit-handler')) {
        e.preventDefault();
        e.stopPropagation();
        const step = target.getAttribute('data-step');
        const nextStepNum = parseInt(step);
        
        // Skip if this is step 3's submit button (step 4)
        if (currentClaimStep === 3 && nextStepNum === 4) {
          return; // Let the step 3 handler deal with it
        }
        
        console.log('Claim step navigation clicked, next step:', nextStepNum);
        handleClaimStep(step);
      }
      
      if (target.matches('.btn-claim-back')) {
        e.preventDefault();
        const step = target.getAttribute('data-step');
        handleClaimStep(step, true);
      }
      
      // Close modal
      if (target.matches('.modal-close') || target.matches('.modal-overlay')) {
        closeModal();
      }
      
      // Cancel button
      if (target.matches('.btn-cancel')) {
        e.preventDefault();
        // Check if it's the claim modal
        const claimModal = document.getElementById('claimModal');
        if (claimModal) {
          claimModal.remove();
          // Reset claim state
          currentClaim = null;
          currentClaimStep = 1;
        } else {
          closeModal();
        }
      }
      
      // Save warranty form
      if (target.matches('.btn-save-warranty')) {
        e.preventDefault();
        saveWarranty();
      }
    });

    // Delegate change events for auto-calculation
    document.addEventListener('change', function(e) {
      const target = e.target;
      
      // Auto-calculate expiration date when series or purchase date changes
      if ((target.matches('#rodSeries') || target.matches('#purchaseDate')) && document.getElementById('warrantyForm')) {
        calculateExpirationDate();
      }
    });
  }

  /**
   * Load warranties from Firebase, API, or localStorage
   */
  async function loadWarranties() {
    if (USE_FIREBASE) {
      try {
        const docRef = db.collection('warranties').doc(customerId.toString());
        const doc = await docRef.get();
        
        if (doc.exists) {
          const data = doc.data();
          warranties = data.warranties || [];
          // Also sync to localStorage as backup
          localStorage.setItem(getStorageKey(), JSON.stringify(warranties));
        } else {
          warranties = [];
        }
      } catch (error) {
        console.error('Error loading warranties from Firebase:', error);
        // Fall back to localStorage if Firebase fails
        const stored = localStorage.getItem(getStorageKey());
        if (stored) {
          try {
            warranties = JSON.parse(stored);
          } catch (e) {
            console.error('Error parsing localStorage:', e);
            warranties = [];
          }
        } else {
          warranties = [];
        }
      }
    } else if (USE_API) {
      try {
        // Support both route-based (Node.js) and query-based (PHP) endpoints
        const url = IS_ROUTE_BASED_API
          ? `${API_ENDPOINT}?customer_id=${customerId}`
          : `${API_ENDPOINT}?action=get&customer_id=${customerId}`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        
        const data = await response.json();
        warranties = data.warranties || [];
        
        // Also sync to localStorage as backup
        localStorage.setItem(getStorageKey(), JSON.stringify(warranties));
      } catch (error) {
        console.error('Error loading warranties from API:', error);
        
        // Fall back to localStorage if API fails
        const stored = localStorage.getItem(getStorageKey());
        if (stored) {
          try {
            warranties = JSON.parse(stored);
          } catch (e) {
            console.error('Error parsing localStorage:', e);
            warranties = [];
          }
        } else {
          warranties = [];
        }
      }
    } else {
      // Fallback to localStorage
      const stored = localStorage.getItem(getStorageKey());
      if (stored) {
        try {
          warranties = JSON.parse(stored);
        } catch (e) {
          console.error('Error loading warranties:', e);
          warranties = [];
        }
      }
    }
  }

  /**
   * Save warranties to Firebase, API, or localStorage
   */
  async function saveWarranties() {
    if (USE_FIREBASE) {
      try {
        const docRef = db.collection('warranties').doc(customerId.toString());
        await docRef.set({
          customerId: customerId,
          customerEmail: customerEmail,
          warranties: warranties,
          lastUpdated: firebase.firestore.FieldValue.serverTimestamp()
        }, { merge: true });
        
        // Also save to localStorage as backup
        localStorage.setItem(getStorageKey(), JSON.stringify(warranties));
      } catch (error) {
        console.error('Error saving warranties to Firebase:', error);
        // Fall back to localStorage
        localStorage.setItem(getStorageKey(), JSON.stringify(warranties));
        alert('Warning: Unable to save to cloud. Data saved locally only.');
      }
    } else if (USE_API) {
      // Declare variables outside try block so they're available in catch
      const payload = IS_ROUTE_BASED_API
        ? { customer_id: customerId, warranties: warranties } // Node.js route-based format
        : { customer_id: customerId, warranties: warranties }; // PHP query-based format (action in URL)
      
      const url = IS_ROUTE_BASED_API 
        ? API_ENDPOINT 
        : `${API_ENDPOINT}?action=save`;
      
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        
        // Get response text first to see what we got
        const responseText = await response.text();
        console.log('API Response Status:', response.status);
        console.log('API Response Text:', responseText);
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status} - ${responseText}`);
        }
        
        // Try to parse JSON
        let result;
        try {
          result = JSON.parse(responseText);
        } catch (e) {
          throw new Error(`Invalid JSON response: ${responseText.substring(0, 100)}`);
        }
        
        // Check if response indicates success
        if (!result.success) {
          throw new Error(result.error || 'Save failed');
        }
        
        // Also save to localStorage as backup
        localStorage.setItem(getStorageKey(), JSON.stringify(warranties));
        console.log('Warranties saved successfully to server');
      } catch (error) {
        console.error('Error saving warranties to API:', error);
        console.error('Error details:', {
          message: error.message,
          url: url,
          payload: payload
        });
        // Fall back to localStorage
        localStorage.setItem(getStorageKey(), JSON.stringify(warranties));
        alert('Warning: Unable to save to server. Data saved locally only.\n\nError: ' + error.message + '\n\nCheck browser console (F12) for details.');
      }
    } else {
      // Fallback to localStorage
      localStorage.setItem(getStorageKey(), JSON.stringify(warranties));
    }
  }

  /**
   * Get storage key with customer ID
   */
  function getStorageKey() {
    return customerId ? `${STORAGE_KEY}_${customerId}` : STORAGE_KEY;
  }

  /**
   * Auto-populate warranties from past orders
   */
  async function autoPopulateFromOrders() {
    // Ensure we have the latest warranties loaded from DB first
    // This prevents duplicates if warranties were already saved
    await loadWarranties();
    
    console.log('=== AUTO-POPULATE START ===');
    console.log('Total customer orders:', customerOrders.length);
    console.log('Current warranties count (after reload):', warranties.length);
    
    // Log all orders for debugging
    if (customerOrders.length > 0) {
      console.log('All orders:');
      customerOrders.forEach((order, index) => {
        console.log(`  Order ${index + 1}:`, {
          id: order.id,
          orderNumber: order.orderNumber,
          financialStatus: order.financialStatus,
          createdAt: order.createdAt,
          lineItems: order.lineItems.map(item => item.title)
        });
      });
    } else {
      console.warn('⚠️ No orders found in customerOrders array!');
      console.warn('This might mean:');
      console.warn('  1. The order hasn\'t synced to customer.orders yet (wait a few minutes)');
      console.warn('  2. The Liquid template isn\'t passing orders correctly');
      return;
    }
    
    const rodOrders = [];
    
    // Find all orders with rods (include ALL orders regardless of financial status)
    // Use isRodProduct() to check both "rod" keyword and known product names
    customerOrders.forEach(order => {
      const rodItems = order.lineItems.filter(item => {
        const isRod = isRodProduct(item.title);
        if (isRod) {
          console.log(`  ✓ Found rod in order #${order.orderNumber}: "${item.title}"`);
        }
        return isRod;
      });
      
      if (rodItems.length > 0) {
        rodOrders.push({
          order: order,
          rodItems: rodItems
        });
        console.log(`✓ Rod order found: #${order.orderNumber} (Status: ${order.financialStatus})`, {
          rodItems: rodItems.map(item => item.title)
        });
      }
    });
    
    console.log(`Found ${rodOrders.length} order(s) with rods`);
    
    if (rodOrders.length === 0) {
      console.warn('⚠️ No orders with rods found!');
      console.warn('Check if product titles contain the word "rod" (case-insensitive)');
      return;
    }
    
    let newWarrantiesCount = 0;
    
    // Auto-create warranties for each rod found in orders
    for (const { order, rodItems } of rodOrders) {
      for (const item of rodItems) {
        // IMPORTANT: Only match rod series from PRODUCT title, NOT variant title
        // Variant titles like "Casting, 7'0" med/hvy - fast" should NOT match series names
        const productTitle = item.title || '';
        const variantTitle = item.variantTitle || '';
        let rodSeries = 'Other/Discontinued';
        
        // Match product title to warranty series (ONLY check product title, never variant)
        for (const series of WARRANTY_SERIES_OPTIONS) {
          if (series !== 'Other/Discontinued' && productTitle.toLowerCase().includes(series.toLowerCase())) {
            rodSeries = series;
            break;
          }
        }
        
        // Use variantTitle for rodModel if available, otherwise use product title
        // Variant title contains details like "Casting, 7'0" med/hvy - fast"
        const rodModel = (variantTitle && variantTitle !== 'Default Title') 
          ? variantTitle 
          : productTitle;
        
        console.log(`  Processing: Product="${productTitle}", Variant="${variantTitle || 'N/A'}"`);
        console.log(`    → Determined Series: "${rodSeries}", Model: "${rodModel}"`);
        
        // Skip if this warranty already exists
        // Check by: orderId + rodModel combination (must match exactly)
        const existingWarranty = warranties.find(w => {
          // Must be from the same order
          if (!w.orderId || w.orderId.toString() !== order.id.toString()) {
            return false;
          }
          // Must have the same rod model (variant) - exact match
          if (w.rodModel && rodModel) {
            const match = w.rodModel.toLowerCase().trim() === rodModel.toLowerCase().trim();
            if (match) {
              console.log(`    → Found existing warranty: Series="${w.rodSeries}", Model="${w.rodModel}"`);
            }
            return match;
          }
          return false;
        });
        
        if (existingWarranty) {
          console.log(`  ⏭️ Skipping - warranty already exists for order #${order.orderNumber}, model: "${rodModel}"`);
          continue;
        }
        
        console.log(`  ✨ Creating new warranty for order #${order.orderNumber}, product: "${productTitle}", model: "${rodModel}"`);
        
        // Calculate expiration date
        let expirationDate = null;
        const purchaseDate = order.createdAt;
        const seriesData = WARRANTY_SERIES[rodSeries];
        
        if (seriesData && seriesData.duration > 0 && purchaseDate) {
          const purchase = new Date(purchaseDate);
          const expiration = new Date(purchase);
          expiration.setFullYear(purchase.getFullYear() + seriesData.duration);
          
          const year = expiration.getFullYear();
          const month = String(expiration.getMonth() + 1).padStart(2, '0');
          const day = String(expiration.getDate()).padStart(2, '0');
          expirationDate = `${year}-${month}-${day}`;
        }
        
        const warranty = {
          id: generateId(),
          rodSeries: rodSeries,
          rodModel: rodModel,
          serialNumber: '',
          purchaseDate: purchaseDate,
          expirationDate: expirationDate,
          purchaseRetailer: 'Duckett Fishing Online Store',
          receiptFile: null,
          receiptFileName: '',
          orderId: order.id,
          orderNumber: order.orderNumber,
          notes: `Auto-created from Order #${order.orderNumber}`,
          createdAt: new Date().toISOString(),
          autoCreated: true
        };
        
        warranties.push(warranty);
        newWarrantiesCount++;
        console.log(`  ✓ Warranty added (Series: ${rodSeries}, Model: ${rodModel})`);
      }
    }
    
    console.log(`=== AUTO-POPULATE RESULTS ===`);
    console.log(`New warranties created: ${newWarrantiesCount}`);
    console.log(`Total warranties now: ${warranties.length}`);
    
    // Save the auto-created warranties only if new ones were added
    if (newWarrantiesCount > 0) {
      console.log('Saving warranties to database...');
      try {
        await saveWarranties();
        console.log(`✓ Successfully saved ${newWarrantiesCount} new warranty(ies) to database`);
      } catch (error) {
        console.error('❌ ERROR saving warranties:', error);
        alert('Warning: Warranties were created but could not be saved to server. Error: ' + error.message);
      }
    } else {
      console.log('No new warranties to save (all already exist)');
    }
  }

  /**
   * Render the dashboard
   */
  function renderDashboard() {
    if (!dashboardEl) return;

    const stats = calculateStats();
    
    dashboardEl.innerHTML = `
      <div class="warranty-actions">
        <h2>My Warranties</h2>
        <button class="btn btn-primary btn-add-warranty">
          + Add New Warranty
        </button>
      </div>

      <div class="warranty-stats">
        <div class="stat-card">
          <div class="stat-number">${stats.total}</div>
          <div class="stat-label">Total Warranties</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">${stats.active}</div>
          <div class="stat-label">Active</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">${stats.expiring}</div>
          <div class="stat-label">Expiring Soon</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">${stats.expired}</div>
          <div class="stat-label">Expired</div>
        </div>
      </div>

      ${renderWarrantiesList()}
    `;
  }

  /**
   * Calculate warranty statistics
   */
  function calculateStats() {
    const now = new Date();
    const expiringDate = new Date();
    expiringDate.setDate(now.getDate() + EXPIRING_DAYS);

    let active = 0;
    let expiring = 0;
    let expired = 0;

    warranties.forEach(warranty => {
      if (!warranty.expirationDate) return;
      
      const expDate = new Date(warranty.expirationDate);
      
      if (expDate < now) {
        expired++;
      } else if (expDate <= expiringDate) {
        expiring++;
        active++;
      } else {
        active++;
      }
    });

    return {
      total: warranties.length,
      active,
      expiring,
      expired
    };
  }

  /**
   * Render warranties list
   */
  function renderWarrantiesList() {
    if (warranties.length === 0) {
      return `
        <div class="empty-state">
          <div class="empty-state-icon">🎣</div>
          <h3>No Warranties Yet</h3>
          <p>Start managing your rod warranties by adding your first one.</p>
          <button class="btn btn-primary btn-add-warranty">
            Add Your First Warranty
          </button>
        </div>
      `;
    }

    // Sort warranties: expiring soon first, then by expiration date
    const sortedWarranties = [...warranties].sort((a, b) => {
      if (!a.expirationDate && !b.expirationDate) return 0;
      if (!a.expirationDate) return 1;
      if (!b.expirationDate) return -1;
      return new Date(a.expirationDate) - new Date(b.expirationDate);
    });

    return `
      <div class="warranties-grid">
        ${sortedWarranties.map(warranty => renderWarrantyCard(warranty)).join('')}
      </div>
    `;
  }

  /**
   * Render a single warranty card
   */
  function renderWarrantyCard(warranty) {
    const status = getWarrantyStatus(warranty);
    const statusClass = status.class;
    const statusText = status.text;
    
    const cardClass = `warranty-card ${status.cardClass || ''}`;
    
    return `
      <div class="${cardClass}">
        <div class="warranty-card-header">
          <h3 class="warranty-card-title">${escapeHtml(warranty.rodModel || 'Unnamed Rod')}</h3>
          <span class="warranty-status-badge ${statusClass}">${statusText}</span>
        </div>
        
        <div class="warranty-card-details">
          ${warranty.rodSeries ? `
            <div class="warranty-detail-row">
              <span class="warranty-detail-label">Rod Series</span>
              <span class="warranty-detail-value">${escapeHtml(warranty.rodSeries)}</span>
            </div>
          ` : ''}
          
          ${getWarrantyTier(warranty).text ? `
            <div class="warranty-detail-row">
              <span class="warranty-detail-label">Warranty Tier</span>
              <span class="warranty-detail-value">${getWarrantyTier(warranty).text}</span>
            </div>
          ` : ''}
          
          ${warranty.serialNumber ? `
            <div class="warranty-detail-row">
              <span class="warranty-detail-label">Serial Number</span>
              <span class="warranty-detail-value">${escapeHtml(warranty.serialNumber)}</span>
            </div>
          ` : ''}
          
          ${warranty.purchaseDate ? `
            <div class="warranty-detail-row">
              <span class="warranty-detail-label">Purchase Date</span>
              <span class="warranty-detail-value">${formatDate(warranty.purchaseDate)}</span>
            </div>
          ` : ''}
          
          ${warranty.expirationDate ? `
            <div class="warranty-detail-row">
              <span class="warranty-detail-label">Expiration Date</span>
              <span class="warranty-detail-value">${formatDate(warranty.expirationDate)}</span>
            </div>
          ` : ''}
          
          ${warranty.purchaseRetailer ? `
            <div class="warranty-detail-row">
              <span class="warranty-detail-label">Retailer</span>
              <span class="warranty-detail-value">${escapeHtml(warranty.purchaseRetailer)}</span>
            </div>
          ` : ''}
          
          ${warranty.orderNumber ? `
            <div class="warranty-detail-row">
              <span class="warranty-detail-label">Shopify Order</span>
              <span class="warranty-detail-value">#${escapeHtml(warranty.orderNumber)} ✓ Verified</span>
            </div>
          ` : warranty.receiptFile ? `
            <div class="warranty-detail-row">
              <span class="warranty-detail-label">Receipt</span>
              <span class="warranty-detail-value">
                ${warranty.receiptFile.startsWith('data:image') ? `
                  <a href="${warranty.receiptFile}" target="_blank" class="receipt-link">View Receipt</a>
                ` : `
                  <span class="receipt-file-name">${escapeHtml(warranty.receiptFileName || 'Receipt uploaded')}</span>
                `}
              </span>
            </div>
          ` : ''}
          
          ${warranty.notes ? `
            <div class="warranty-detail-row">
              <span class="warranty-detail-label">Notes</span>
              <span class="warranty-detail-value">${escapeHtml(warranty.notes.substring(0, 50))}${warranty.notes.length > 50 ? '...' : ''}</span>
            </div>
          ` : ''}
        </div>
        
        <div class="warranty-card-actions">
          <button class="btn btn-primary btn-small btn-start-warranty-claim" data-id="${warranty.id}" style="margin-right: auto;">
            🛠️ Start Warranty Claim
          </button>
          <button class="btn btn-secondary btn-small btn-print-warranty-form" data-id="${warranty.id}">
            📄 Print Form
          </button>
          <button class="btn btn-secondary btn-small btn-edit-warranty" data-id="${warranty.id}">
            Edit
          </button>
          <button class="btn btn-danger btn-small btn-delete-warranty" data-id="${warranty.id}">
            Delete
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Get warranty status
   */
  function getWarrantyStatus(warranty) {
    if (!warranty.expirationDate) {
      return {
        class: 'status-active',
        text: 'Active',
        cardClass: ''
      };
    }

    const now = new Date();
    const expDate = new Date(warranty.expirationDate);
    const expiringDate = new Date();
    expiringDate.setDate(now.getDate() + EXPIRING_DAYS);

    if (expDate < now) {
      return {
        class: 'status-expired',
        text: 'Expired',
        cardClass: 'expired'
      };
    } else if (expDate <= expiringDate) {
      const daysLeft = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
      return {
        class: 'status-expiring',
        text: `Expires in ${daysLeft} days`,
        cardClass: 'expiring-soon'
      };
    } else {
      return {
        class: 'status-active',
        text: 'Active',
        cardClass: ''
      };
    }
  }

  /**
   * Show add warranty modal
   */
  function showAddWarrantyModal() {
    editingWarrantyId = null;
    showWarrantyModal({
      rodModel: '',
      rodSeries: '',
      serialNumber: '',
      purchaseDate: '',
      expirationDate: '',
      purchaseRetailer: '',
      receiptFile: null,
      receiptFileName: '',
      orderId: null,
      orderNumber: null,
      notes: ''
    });
  }

  /**
   * Show edit warranty modal
   */
  function showEditWarrantyModal(warrantyId) {
    const warranty = warranties.find(w => w.id === warrantyId);
    if (!warranty) return;
    
    editingWarrantyId = warrantyId;
    showWarrantyModal(warranty);
  }

  /**
   * Show warranty modal with form
   */
  function showWarrantyModal(warranty) {
    const isEdit = editingWarrantyId !== null;
    const hasShopifyOrder = warranty.orderId !== null && warranty.orderId !== undefined;
    const showOrderSection = customerOrders.length > 0 && !isEdit;
    
    const modalHTML = `
      <style>
        #warrantyModal input[type="text"],
        #warrantyModal input[type="date"],
        #warrantyModal input[type="tel"],
        #warrantyModal input[type="email"],
        #warrantyModal textarea,
        #warrantyModal select {
          background: #ffffff !important;
          color: #1a1a1a !important;
          -webkit-text-fill-color: #1a1a1a !important;
        }
        #warrantyModal input::placeholder,
        #warrantyModal textarea::placeholder {
          color: #999 !important;
          opacity: 1 !important;
        }
        #warrantyModal input::-webkit-input-placeholder,
        #warrantyModal textarea::-webkit-input-placeholder {
          color: #999 !important;
          opacity: 1 !important;
        }
        #warrantyModal input::-moz-placeholder,
        #warrantyModal textarea::-moz-placeholder {
          color: #999 !important;
          opacity: 1 !important;
        }
        #warrantyModal input:-ms-input-placeholder,
        #warrantyModal textarea:-ms-input-placeholder {
          color: #999 !important;
          opacity: 1 !important;
        }
        #warrantyModal input:-moz-placeholder,
        #warrantyModal textarea:-moz-placeholder {
          color: #999 !important;
          opacity: 1 !important;
        }
        #warrantyModal input[readonly] {
          background: #f5f5f5 !important;
          color: #1a1a1a !important;
        }
      </style>
      <div class="modal-overlay active" id="warrantyModal">
        <div class="modal">
          <div class="modal-header">
            <h2 class="modal-title">${isEdit ? 'Edit' : 'Add'} Warranty</h2>
            <button class="modal-close" aria-label="Close">&times;</button>
          </div>
          
          ${showOrderSection ? `
            <div class="order-select-section">
              <h3>Did you purchase this rod from our website?</h3>
              <p class="form-help">If yes, select your order to automatically fill in purchase details.</p>
              <div class="order-select-container">
                <select id="orderSelect" class="form-select">
                  <option value="">No, I purchased elsewhere or don't have an order</option>
                  ${customerOrders.map(order => {
                    const rodItems = order.lineItems.filter(item => 
                      item.title && isRodProduct(item.title)
                    );
                    if (rodItems.length === 0) return '';
                    
                    return `
                      <option value="${order.id}" data-order-number="${order.orderNumber}" data-order-date="${order.createdAt}">
                        Order #${order.orderNumber} - ${formatDate(order.createdAt)} (${rodItems.length} rod${rodItems.length > 1 ? 's' : ''})
                      </option>
                    `;
                  }).join('')}
                </select>
                <button type="button" id="btnUseOrder" class="btn btn-secondary btn-small" style="margin-top: 0.5rem;">
                  Use This Order
                </button>
              </div>
              <div class="order-divider">OR</div>
            </div>
          ` : ''}
          
          ${hasShopifyOrder ? `
            <div class="alert alert-success" style="background: #d4edda; color: #155724; padding: 0.75rem; border-radius: 4px; margin-bottom: 1rem; border: 1px solid #c3e6cb; font-size: 0.9rem;">
              <strong>✓ Purchase verified from Shopify Order #${warranty.orderNumber || 'N/A'}</strong>
              <input type="hidden" id="orderId" name="orderId" value="${warranty.orderId}">
              <input type="hidden" id="orderNumber" name="orderNumber" value="${warranty.orderNumber || ''}">
            </div>
          ` : ''}
          
          <form id="warrantyForm" style="display: grid; gap: 0.75rem;">
            <div class="form-group" style="margin-bottom: 0.75rem;">
              <label class="form-label" for="rodSeries" style="margin-bottom: 0.35rem; font-size: 0.9rem;">Rod Series *</label>
              <select 
                id="rodSeries" 
                name="rodSeries" 
                class="form-select" 
                required
                style="padding: 0.6rem; font-size: 0.9rem; background: #ffffff; color: #1a1a1a; border: 2px solid #e0e0e0;"
              >
                <option value="">Select a series</option>
                ${WARRANTY_SERIES_OPTIONS.map(series => `
                  <option value="${escapeHtml(series)}" ${warranty.rodSeries === series ? 'selected' : ''}>
                    ${escapeHtml(series)}
                  </option>
                `).join('')}
              </select>
              <span class="form-help" style="font-size: 0.75rem; color: #888; margin-top: 0.2rem; display: block;">Auto-calculates expiration date</span>
            </div>
            
            <div class="form-group" style="margin-bottom: 0.75rem;">
              <label class="form-label" for="rodModel" style="margin-bottom: 0.35rem; font-size: 0.9rem;">Rod Model (Variant) *</label>
              <input 
                type="text" 
                id="rodModel" 
                name="rodModel" 
                class="form-input" 
                value="${escapeHtml(warranty.rodModel || '')}"
                required
                placeholder="e.g., Casting, 7'0&quot; med/hvy - fast"
                style="padding: 0.6rem; font-size: 0.9rem; background: #ffffff; color: #1a1a1a; border: 2px solid #e0e0e0;"
              >
            </div>
            
            <div class="form-group" style="margin-bottom: 0.75rem;">
              <label class="form-label" for="serialNumber" style="margin-bottom: 0.35rem; font-size: 0.9rem;">Serial Number</label>
              <input 
                type="text" 
                id="serialNumber" 
                name="serialNumber" 
                class="form-input" 
                value="${escapeHtml(warranty.serialNumber || '')}"
                placeholder="Optional"
                style="padding: 0.6rem; font-size: 0.9rem; background: #ffffff; color: #1a1a1a; border: 2px solid #e0e0e0;"
              >
            </div>
            
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; margin-bottom: 0.75rem;">
              <div class="form-group" style="margin-bottom: 0;">
                <label class="form-label" for="purchaseDate" style="margin-bottom: 0.35rem; font-size: 0.9rem;">Purchase Date *</label>
                <input 
                  type="date" 
                  id="purchaseDate" 
                  name="purchaseDate" 
                  class="form-input" 
                  value="${warranty.purchaseDate || ''}"
                  required
                  style="padding: 0.6rem; font-size: 0.9rem; background: #ffffff; color: #1a1a1a; border: 2px solid #e0e0e0;"
                >
              </div>
              
              <div class="form-group" style="margin-bottom: 0;">
                <label class="form-label" for="expirationDate" style="margin-bottom: 0.35rem; font-size: 0.9rem;">Expiration Date</label>
                <input 
                  type="date" 
                  id="expirationDate" 
                  name="expirationDate" 
                  class="form-input" 
                  value="${warranty.expirationDate || ''}"
                  readonly
                  style="padding: 0.6rem; font-size: 0.9rem; background: #f5f5f5;"
                >
              </div>
            </div>
            
            <div class="form-group" style="margin-bottom: 0.75rem;">
              <label class="form-label" for="purchaseRetailer" style="margin-bottom: 0.35rem; font-size: 0.9rem;">Purchase Retailer</label>
              <input 
                type="text" 
                id="purchaseRetailer" 
                name="purchaseRetailer" 
                class="form-input" 
                value="${escapeHtml(warranty.purchaseRetailer || '')}"
                placeholder="Optional"
                style="padding: 0.6rem; font-size: 0.9rem;"
              >
            </div>
            
            <div class="form-group" style="margin-bottom: 0.75rem;">
              <label class="form-label" for="receiptFile" style="margin-bottom: 0.35rem; font-size: 0.9rem;">
                Proof of Purchase (Receipt) ${!hasShopifyOrder ? '*' : ''}
              </label>
              ${warranty.receiptFile ? `
                <div class="receipt-preview" style="margin-bottom: 0.5rem;">
                  <span class="receipt-file-name" style="font-size: 0.85rem;">${escapeHtml(warranty.receiptFileName || 'receipt')}</span>
                  <button type="button" class="btn-remove-receipt btn-small" style="margin-left: 0.5rem; padding: 0.25rem 0.5rem; font-size: 0.8rem;">Remove</button>
                  ${warranty.receiptFile.startsWith('data:image') ? `
                    <img src="${warranty.receiptFile}" alt="Receipt" class="receipt-image-preview" style="max-width: 100%; margin-top: 0.5rem; border-radius: 4px; max-height: 100px;">
                  ` : ''}
                  <input type="hidden" id="existingReceiptFile" name="existingReceiptFile" value="${warranty.receiptFile}">
                  <input type="hidden" id="existingReceiptFileName" name="existingReceiptFileName" value="${escapeHtml(warranty.receiptFileName || '')}">
                </div>
              ` : ''}
              <input 
                type="file" 
                id="receiptFile" 
                name="receiptFile" 
                class="form-input form-file-input" 
                accept="image/*,.pdf"
                ${!hasShopifyOrder ? 'required' : ''}
                style="padding: 0.5rem; font-size: 0.85rem;"
              >
              <span class="form-help" style="font-size: 0.75rem; color: #888; margin-top: 0.2rem; display: block;">
                ${hasShopifyOrder 
                  ? 'Receipt not required (order verified)' 
                  : 'Upload receipt photo or PDF (required)'}
              </span>
              <div id="receiptPreview" class="receipt-preview-container" style="display: none; margin-top: 0.5rem;"></div>
            </div>
            
            ${hasShopifyOrder ? `
              <input type="hidden" id="orderId" name="orderId" value="${warranty.orderId}">
              <input type="hidden" id="orderNumber" name="orderNumber" value="${warranty.orderNumber || ''}">
            ` : ''}
            
            <div class="form-group" style="margin-bottom: 0.75rem;">
              <label class="form-label" for="notes" style="margin-bottom: 0.35rem; font-size: 0.9rem;">Notes</label>
              <textarea 
                id="notes" 
                name="notes" 
                class="form-textarea"
                placeholder="Optional additional information"
                style="padding: 0.6rem; font-size: 0.9rem; min-height: 60px; resize: vertical; background: #ffffff; color: #1a1a1a; border: 2px solid #e0e0e0;"
              >${escapeHtml(warranty.notes || '')}</textarea>
            </div>
            
            <div class="form-actions" style="display: flex; gap: 0.75rem; justify-content: flex-end; margin-top: 0.5rem; padding-top: 1rem; border-top: 1px solid #e0e0e0;">
              <button type="button" class="btn btn-secondary btn-cancel" style="padding: 0.65rem 1.25rem; font-size: 0.9rem;">Cancel</button>
              <button type="submit" class="btn btn-primary btn-save-warranty" style="padding: 0.65rem 1.75rem; font-size: 0.9rem; font-weight: 600;">${isEdit ? 'Update' : 'Save'} Warranty</button>
            </div>
          </form>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    modalOverlay = document.getElementById('warrantyModal');
    
    // Setup order selection
    if (showOrderSection) {
      const orderSelect = modalOverlay.querySelector('#orderSelect');
      const btnUseOrder = modalOverlay.querySelector('#btnUseOrder');
      
      if (btnUseOrder) {
        btnUseOrder.addEventListener('click', function() {
          const selectedOrderId = orderSelect?.value;
          if (!selectedOrderId) return;
          
          const selectedOrder = customerOrders.find(o => o.id.toString() === selectedOrderId);
          if (!selectedOrder) return;
          
          // Find rod products in the order
          const rodItems = selectedOrder.lineItems.filter(item => 
            item.title && isRodProduct(item.title)
          );
          
          if (rodItems.length === 0) {
            alert('No rods found in this order.');
            return;
          }
          
          // Auto-populate from order
          const rodModelInput = modalOverlay.querySelector('#rodModel');
          const purchaseDateInput = modalOverlay.querySelector('#purchaseDate');
          const receiptFileInput = modalOverlay.querySelector('#receiptFile');
          const orderIdInput = modalOverlay.querySelector('#orderId') || document.createElement('input');
          const orderNumberInput = modalOverlay.querySelector('#orderNumber') || document.createElement('input');
          
          // Use variantTitle for rodModel (contains details like "Casting, 7'0" med/hvy - fast")
          // Fall back to product title if variantTitle is not available
          if (rodModelInput && rodItems.length > 0) {
            const selectedItem = rodItems[0];
            rodModelInput.value = (selectedItem.variantTitle && selectedItem.variantTitle !== 'Default Title') 
              ? selectedItem.variantTitle 
              : selectedItem.title;
          }
          
          if (purchaseDateInput) {
            purchaseDateInput.value = selectedOrder.createdAt;
          }
          
          // Mark as Shopify order
          orderIdInput.type = 'hidden';
          orderIdInput.id = 'orderId';
          orderIdInput.name = 'orderId';
          orderIdInput.value = selectedOrder.id;
          if (!modalOverlay.querySelector('#orderId')) {
            modalOverlay.querySelector('form').appendChild(orderIdInput);
          }
          
          orderNumberInput.type = 'hidden';
          orderNumberInput.id = 'orderNumber';
          orderNumberInput.name = 'orderNumber';
          orderNumberInput.value = selectedOrder.orderNumber.toString();
          if (!modalOverlay.querySelector('#orderNumber')) {
            modalOverlay.querySelector('form').appendChild(orderNumberInput);
          }
          
          // Remove receipt requirement
          if (receiptFileInput) {
            receiptFileInput.removeAttribute('required');
          }
          
          // Hide order selection section and show success message
          const orderSection = modalOverlay.querySelector('.order-select-section');
          if (orderSection) {
            orderSection.innerHTML = `
              <div class="alert alert-success">
                ✓ Using Shopify Order #${selectedOrder.orderNumber} - Purchase verified
              </div>
            `;
          }
          
          // Auto-calculate expiration
          calculateExpirationDate();
          
          alert(`Warranty form pre-filled from Order #${selectedOrder.orderNumber}. Please select the rod series to complete.`);
        });
      }
    }
    
    // Setup receipt file preview
    const receiptFileInput = modalOverlay.querySelector('#receiptFile');
    if (receiptFileInput) {
      receiptFileInput.addEventListener('change', function(e) {
        const file = e.target.files[0];
        if (!file) return;
        
        const previewContainer = modalOverlay.querySelector('#receiptPreview');
        if (!previewContainer) return;
        
        const reader = new FileReader();
        reader.onload = function(e) {
          previewContainer.innerHTML = `
            <div class="receipt-preview">
              <span class="receipt-file-name">${escapeHtml(file.name)}</span>
              ${file.type.startsWith('image/') ? `
                <img src="${e.target.result}" alt="Receipt preview" class="receipt-image-preview">
              ` : ''}
            </div>
          `;
          previewContainer.style.display = 'block';
        };
        
        if (file.type.startsWith('image/')) {
          reader.readAsDataURL(file);
        } else {
          previewContainer.innerHTML = `<div class="receipt-preview"><span class="receipt-file-name">${escapeHtml(file.name)}</span></div>`;
          previewContainer.style.display = 'block';
        }
      });
    }
    
    // Remove receipt button
    const removeReceiptBtn = modalOverlay.querySelector('.btn-remove-receipt');
    if (removeReceiptBtn) {
      removeReceiptBtn.addEventListener('click', function() {
        const preview = this.closest('.receipt-preview');
        if (preview) preview.remove();
        if (receiptFileInput) receiptFileInput.value = '';
        const hiddenInputs = modalOverlay.querySelectorAll('[name="existingReceiptFile"], [name="existingReceiptFileName"]');
        hiddenInputs.forEach(input => input.remove());
        if (!modalOverlay.querySelector('[name="orderId"]')) {
          receiptFileInput.setAttribute('required', 'required');
        }
      });
    }
    
    // Calculate expiration date if series and purchase date are present
    if (warranty.rodSeries && warranty.purchaseDate) {
      calculateExpirationDate();
    }
    
    // Focus first input
    const firstInput = modalOverlay.querySelector('.form-select');
    if (firstInput) firstInput.focus();
  }

  /**
   * Close modal
   */
  function closeModal() {
    if (modalOverlay) {
      modalOverlay.remove();
      modalOverlay = null;
    }
    editingWarrantyId = null;
  }

  /**
   * Save warranty
   */
  async function saveWarranty() {
    const form = document.getElementById('warrantyForm');
    if (!form || !form.checkValidity()) {
      form.reportValidity();
      return;
    }

    // Check receipt requirement
    const orderId = form.querySelector('[name="orderId"]')?.value;
    const receiptFileInput = form.querySelector('#receiptFile');
    const existingReceipt = form.querySelector('[name="existingReceiptFile"]')?.value;
    const receiptFile = receiptFileInput?.files[0];
    
    // Receipt is required unless there's a Shopify order
    if (!orderId && !receiptFile && !existingReceipt) {
      alert('Please upload a receipt to verify your purchase date. This is required for warranty registration.');
      receiptFileInput?.focus();
      return;
    }
    
    // Read receipt file if new file uploaded
    let receiptFileData = existingReceipt || null;
    let receiptFileName = form.querySelector('[name="existingReceiptFileName"]')?.value || '';
    
    if (receiptFile) {
      const reader = new FileReader();
      reader.readAsDataURL(receiptFile);
      
      reader.onload = async function() {
        receiptFileData = reader.result;
        receiptFileName = receiptFile.name;
        try {
          await saveWarrantyData(receiptFileData, receiptFileName);
        } catch (error) {
          console.error('Error saving warranty:', error);
          alert('Error saving warranty: ' + error.message);
        }
      };
      
      reader.onerror = function() {
        alert('Error reading receipt file. Please try again.');
        return;
      };
    } else {
      try {
        await saveWarrantyData(receiptFileData, receiptFileName);
      } catch (error) {
        console.error('Error saving warranty:', error);
        alert('Error saving warranty: ' + error.message);
      }
    }
    
    async function saveWarrantyData(receiptFileData, receiptFileName) {
      const formData = new FormData(form);
      const warrantyData = {
        rodSeries: formData.get('rodSeries'),
        rodModel: formData.get('rodModel').trim(),
        serialNumber: formData.get('serialNumber').trim(),
        purchaseDate: formData.get('purchaseDate'),
        expirationDate: formData.get('expirationDate') || null,
        purchaseRetailer: formData.get('purchaseRetailer').trim(),
        receiptFile: receiptFileData,
        receiptFileName: receiptFileName,
        orderId: formData.get('orderId') || null,
        orderNumber: formData.get('orderNumber') || null,
        notes: formData.get('notes').trim()
      };

      // Ensure we have all warranties loaded before saving (reload from API/DB to get latest)
      await loadWarranties();
      
      if (editingWarrantyId) {
        // Update existing
        const index = warranties.findIndex(w => w.id === editingWarrantyId);
        if (index !== -1) {
          warranties[index] = { ...warranties[index], ...warrantyData };
        }
      } else {
        // Create new
        warrantyData.id = generateId();
        warrantyData.createdAt = new Date().toISOString();
        
        // Check if warranty with same orderId and rodModel already exists (prevent duplicates)
        const isDuplicate = warranties.some(w => 
          w.orderId && warrantyData.orderId && 
          w.orderId.toString() === warrantyData.orderId.toString() &&
          w.rodModel && warrantyData.rodModel &&
          w.rodModel.toLowerCase().trim() === warrantyData.rodModel.toLowerCase().trim()
        );
        
        if (!isDuplicate) {
          warranties.push(warrantyData);
        }
      }

      await saveWarranties();
      renderDashboard();
      closeModal();
    }
  }

  /**
   * Delete warranty
   */
  async function deleteWarranty(warrantyId) {
    if (!confirm('Are you sure you want to delete this warranty? This action cannot be undone.')) {
      return;
    }

    warranties = warranties.filter(w => w.id !== warrantyId);
    await saveWarranties();
    renderDashboard();
  }

  /**
   * Print warranty form - opens a printable form with auto-filled warranty data
   */
  // Make printWarrantyForm globally accessible for onclick handlers
  window.printWarrantyForm = function printWarrantyForm(warrantyId) {
    const warranty = warranties.find(w => w.id === warrantyId);
    if (!warranty) {
      alert('Warranty not found.');
      return;
    }

    // Format dates for display
    const formatDateForForm = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      });
    };

    // Get current date for form submission date
    const today = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    });

    // Create printable warranty form HTML matching the actual Duckett Fishing form
    const formHTML = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Duckett Fishing Warranty Form - 2026</title>
  <style>
    @media print {
      body { margin: 0; padding: 0.5in; }
      .no-print { display: none; }
      @page { margin: 0.5in; }
    }
    body {
      font-family: Arial, sans-serif;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 20px;
      line-height: 1.4;
      color: #000;
      font-size: 12px;
    }
    .form-title {
      text-align: center;
      font-size: 20px;
      font-weight: bold;
      margin-bottom: 10px;
    }
    .disclaimer {
      font-size: 10px;
      text-align: center;
      margin-bottom: 20px;
      line-height: 1.3;
    }
    .form-section {
      margin-bottom: 15px;
    }
    .form-row {
      display: flex;
      margin-bottom: 10px;
      gap: 15px;
      align-items: flex-end;
    }
    .form-group {
      flex: 1;
    }
    .form-group-wide {
      flex: 2;
    }
    .form-label {
      display: block;
      font-size: 11px;
      margin-bottom: 3px;
      font-weight: 600;
    }
    .form-field {
      border-bottom: 1px solid #000;
      min-height: 20px;
      padding: 2px 0;
      font-size: 11px;
    }
    .checkbox-group {
      display: flex;
      gap: 20px;
      margin-top: 8px;
      flex-wrap: wrap;
    }
    .checkbox-item {
      display: flex;
      align-items: center;
      gap: 5px;
      font-size: 11px;
    }
    .checkbox-item input[type="checkbox"] {
      width: 12px;
      height: 12px;
    }
    .guide-policy {
      background: #f0f0f0;
      padding: 8px;
      margin: 15px 0;
      font-size: 10px;
      border-left: 3px solid #666;
    }
    .instructions-box {
      background: #d3d3d3;
      padding: 12px;
      margin: 15px 0;
      font-size: 11px;
    }
    .instructions-box h3 {
      margin: 0 0 8px 0;
      font-size: 12px;
    }
    .instructions-box ol {
      margin: 8px 0;
      padding-left: 20px;
    }
    .instructions-box li {
      margin-bottom: 6px;
    }
    .rod-example {
      text-align: center;
      margin: 10px 0;
      padding: 10px;
      border: 1px solid #999;
      background: #f9f9f9;
      font-size: 10px;
    }
    .warranty-table {
      width: 100%;
      border-collapse: collapse;
      margin: 15px 0;
      font-size: 10px;
    }
    .warranty-table th,
    .warranty-table td {
      border: 1px solid #000;
      padding: 6px;
      text-align: left;
    }
    .warranty-table th {
      background: #e0e0e0;
      font-weight: bold;
    }
    .warranty-table td:last-child {
      text-align: center;
    }
    .shipping-note {
      background: #fffacd;
      padding: 8px;
      margin: 15px 0;
      font-size: 11px;
      font-weight: 600;
      border: 1px solid #ddd;
    }
    .mailing-address {
      font-weight: 600;
      margin-top: 8px;
    }
    .notes-field {
      min-height: 80px;
      border: 1px solid #000;
      padding: 5px;
      width: 100%;
      font-family: Arial, sans-serif;
      font-size: 11px;
    }
    .print-actions {
      text-align: center;
      margin: 30px 0;
      padding: 20px;
      background: #f0f0f0;
      border-radius: 5px;
    }
    .btn-print {
      background: #0066cc;
      color: white;
      border: none;
      padding: 12px 30px;
      font-size: 16px;
      border-radius: 5px;
      cursor: pointer;
      margin: 0 10px;
    }
    .btn-print:hover {
      background: #0052a3;
    }
    .btn-close {
      background: #666;
      color: white;
      border: none;
      padding: 12px 30px;
      font-size: 16px;
      border-radius: 5px;
      cursor: pointer;
    }
    .btn-close:hover {
      background: #555;
    }
  </style>
</head>
<body>
  <div class="form-title">Duckett Fishing Warranty Form - 2026</div>
  <div class="disclaimer">
    See Website for complete Details Proof of Purchase required. ALL Rods have a minimum 1 year warranty. 
    Past 1 year, rods may no longer be covered under warranty, or may require an additional fees. 
    Replacement fees can be waived with proof of purchase within 1 year of sale.
  </div>

  <div class="no-print" style="background: #e8f4f8; padding: 15px; margin-bottom: 20px; border-radius: 5px;">
    <strong>Form Information:</strong> The fields below have been pre-filled from your warranty record. 
    Review and complete any missing information before printing.
  </div>

  <div class="form-section">
    <div class="form-row">
      <div class="form-group-wide">
        <label class="form-label">Name:</label>
        <div class="form-field">${escapeHtml(customerEmail ? customerEmail.split('@')[0].replace(/[._]/g, ' ') : '')}</div>
      </div>
    </div>
    <div class="form-row">
      <div class="form-group-wide">
        <label class="form-label">Address (NO P.O. BOX):</label>
        <div class="form-field">&nbsp;</div>
      </div>
    </div>
    <div class="form-row">
      <div class="form-group">
        <label class="form-label">City:</label>
        <div class="form-field">&nbsp;</div>
      </div>
      <div class="form-group">
        <label class="form-label">State:</label>
        <div class="form-field">&nbsp;</div>
      </div>
      <div class="form-group">
        <label class="form-label">Zip:</label>
        <div class="form-field">&nbsp;</div>
      </div>
    </div>
    <div class="form-row">
      <div class="form-group">
        <label class="form-label">Phone Number:</label>
        <div class="form-field">&nbsp;</div>
      </div>
      <div class="form-group-wide">
        <label class="form-label">Email (Required for Tracking):</label>
        <div class="form-field">${escapeHtml(customerEmail || '')}</div>
      </div>
    </div>
  </div>

  <div class="form-section">
    <div class="form-row">
      <div class="form-group-wide">
        <label class="form-label">Rod Name or Model:</label>
        <div class="form-field"><strong>${escapeHtml(warranty.rodSeries || '')}${warranty.rodModel ? ' - ' + escapeHtml(warranty.rodModel) : ''}</strong></div>
      </div>
    </div>
  </div>

  <div class="form-section">
    <label class="form-label">Area of Breakage:</label>
    <div class="checkbox-group">
      <div class="checkbox-item"><input type="checkbox" id="breakage-butt"> <label for="breakage-butt">Butt</label></div>
      <div class="checkbox-item"><input type="checkbox" id="breakage-grip"> <label for="breakage-grip">Grip</label></div>
      <div class="checkbox-item"><input type="checkbox" id="breakage-reel"> <label for="breakage-reel">Reel Seat</label></div>
      <div class="checkbox-item"><input type="checkbox" id="breakage-blank"> <label for="breakage-blank">Rod Blank</label></div>
    </div>
  </div>

  <div class="form-section">
    <label class="form-label">Specify Location on Blank:</label>
    <div class="checkbox-group">
      <div class="checkbox-item"><input type="checkbox" id="location-tip"> <label for="location-tip">Tip</label></div>
      <div class="checkbox-item"><input type="checkbox" id="location-middle"> <label for="location-middle">Middle</label></div>
      <div class="checkbox-item"><input type="checkbox" id="location-reel"> <label for="location-reel">Near to Reel Seat</label></div>
    </div>
  </div>

  <div class="guide-policy">
    <strong>Guide Policy:</strong> For replacement guides or tips, please fill out our Tips & Guides form and email to info@duckettfishing.com with the subject "Guide Replacement"
  </div>

  <div class="instructions-box">
    <h3>Instructions:</h3>
    <p><strong>(Do Not Send the Entire Rod.)</strong></p>
    <ol>
      <li><strong>Cut out the 6" section of the rod blank as seen below.</strong>
        <div class="rod-example">
          <div style="font-weight: bold; margin-bottom: 5px;">Example:</div>
          <div>DFMP70MH-C</div>
          <div>Power: Medium Heavy</div>
          <div style="margin-top: 5px; font-size: 9px;">(Look for similar identification on your rod)</div>
        </div>
      </li>
      <li><strong>Package rod section with Warranty Form and receipt.</strong></li>
      <li><strong>Include payment (Check/Money Order) in padded package or include a note to contact you for over-the-phone payment.</strong></li>
      <li><strong>Ship package to our mailing address:</strong>
        <div class="mailing-address">
          Duckett Fishing<br>
          250 Conner Island Pkwy<br>
          Guntersville, AL 35976
        </div>
      </li>
    </ol>
  </div>

  <table class="warranty-table">
    <thead>
      <tr>
        <th>Rod Series</th>
        <th>Full Warranty</th>
        <th>Limited Warranty</th>
        <th>Replacement Fee</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Crappie Slayer | Ghost 2 | Cloak</td>
        <td>1 Year</td>
        <td>1 Year</td>
        <td>$25</td>
      </tr>
      <tr>
        <td>Torch | Silhouette</td>
        <td>1 Year</td>
        <td>1 Year</td>
        <td>$35</td>
      </tr>
      <tr>
        <td>Zeus | Salt Series | Walleye</td>
        <td>1 Year</td>
        <td>3 Years</td>
        <td>$40</td>
      </tr>
      <tr>
        <td>Jacob Wheeler Signature Series</td>
        <td>1 Year</td>
        <td>3 Years</td>
        <td>$50</td>
      </tr>
      <tr>
        <td>Jacob Wheeler 2.0</td>
        <td>1 Year</td>
        <td>3 Years</td>
        <td>$60</td>
      </tr>
      <tr>
        <td>Micro Magic Pro | Incite</td>
        <td>1 Year</td>
        <td>5 Years</td>
        <td>$60</td>
      </tr>
      <tr>
        <td>Pro Series | Wheeler Select</td>
        <td>1 Year</td>
        <td>5 Years</td>
        <td>$75</td>
      </tr>
    </tbody>
  </table>

  <div class="shipping-note">
    ALL Warranty claims require a $29 shipping fee for fulfillment. Include this fee in your package before shipping.
  </div>

  <div class="form-section">
    <label class="form-label">Additional Notes:</label>
    <div class="form-field notes-field">${escapeHtml(warranty.notes || '')}</div>
  </div>

  <div class="print-actions no-print">
    <button class="btn-print" onclick="window.print()">🖨️ Print Form</button>
    <button class="btn-close" onclick="window.close()">Close</button>
  </div>
</body>
</html>`;

    // Open form in new window
    const printWindow = window.open('', '_blank', 'width=800,height=1000');
    if (!printWindow) {
      alert('Please allow pop-ups to print the warranty form.');
      return;
    }
    
    printWindow.document.write(formHTML);
    printWindow.document.close();
    
    // Auto-focus the print window (user can still cancel)
    setTimeout(() => {
      printWindow.focus();
    }, 100);
  }

  /**
   * Calculate warranty fees based on purchase date and warranty tier
   */
  function calculateWarrantyFees(warranty) {
    const seriesData = WARRANTY_SERIES[warranty.rodSeries] || WARRANTY_SERIES['Other/Discontinued'];
    const purchaseDate = warranty.purchaseDate ? new Date(warranty.purchaseDate) : null;
    const today = new Date();
    
    let replacementFee = 0;
    let warrantyTier = 'Full';
    let isWithinWarranty = false;
    
    if (purchaseDate) {
      const oneYearLater = new Date(purchaseDate);
      oneYearLater.setFullYear(purchaseDate.getFullYear() + 1);
      
      // Check if within full warranty (first year)
      if (today < oneYearLater) {
        warrantyTier = 'Full';
        replacementFee = 0; // Free within first year with proof of purchase
        isWithinWarranty = true;
      } else {
        // Check if within limited warranty period
        const warrantyEndDate = new Date(purchaseDate);
        warrantyEndDate.setFullYear(purchaseDate.getFullYear() + seriesData.duration);
        
        if (today < warrantyEndDate) {
          warrantyTier = 'Limited';
          replacementFee = seriesData.replacementFee || 0;
          isWithinWarranty = true;
        } else {
          warrantyTier = 'Expired';
          replacementFee = seriesData.replacementFee || 0; // Still charge fee if out of warranty
          isWithinWarranty = false;
        }
      }
    } else {
      // No purchase date - assume limited warranty
      warrantyTier = 'Limited';
      replacementFee = seriesData.replacementFee || 0;
    }
    
    const shippingFee = 29; // Flat $29 shipping fee for all claims
    const totalFee = replacementFee + shippingFee;
    
    return {
      warrantyTier,
      replacementFee,
      shippingFee,
      totalFee,
      isWithinWarranty,
      seriesData
    };
  }

  /**
   * Start warranty claim process
   */
  let currentClaim = null;
  let currentClaimStep = 1;
  
  function startWarrantyClaim(warrantyId) {
    console.log('startWarrantyClaim called with ID:', warrantyId);
    console.log('Available warranties:', warranties);
    const warranty = warranties.find(w => w.id === warrantyId);
    if (!warranty) {
      console.error('Warranty not found. ID:', warrantyId, 'Available IDs:', warranties.map(w => w.id));
      alert('Warranty not found. Please refresh the page and try again.');
      return;
    }
    
    console.log('Found warranty:', warranty);
    const fees = calculateWarrantyFees(warranty);
    console.log('Calculated fees:', fees);
    
    currentClaim = {
      warrantyId: warranty.id,
      warranty: warranty,
      fees: fees,
      shippingAddress: {},
      claimData: {}
    };
    currentClaimStep = 1;
    
    console.log('Showing claim modal...');
    showClaimModal();
  }

  /**
   * Handle claim step navigation
   */
  function handleClaimStep(nextStep, isBack = false) {
    if (isBack) {
      currentClaimStep = Math.max(1, currentClaimStep - 1);
    } else {
      currentClaimStep = parseInt(nextStep) || currentClaimStep + 1;
    }
    showClaimModal();
  }

  /**
   * Show warranty claim modal
   */
  function showClaimModal() {
    if (!currentClaim) return;
    
    const { warranty, fees } = currentClaim;
    const step = currentClaimStep;
    
    let stepContent = '';
    let showNext = false;
    let showBack = step > 1;
    let nextButtonText = 'Continue';
    
    if (step === 1) {
      // Step 1: Fee breakdown
      stepContent = `
        <div class="claim-step-content" style="padding: 2rem 0;">
          <h3 style="margin-bottom: 2rem; font-size: 1.8rem; font-weight: 700; color: #1a1a1a;">Warranty Fee Breakdown</h3>
          <div style="background: #f8f9fa; padding: 3rem; border-radius: 12px; margin-bottom: 2rem; border: 2px solid #e0e0e0;">
            <div style="margin-bottom: 2rem; font-size: 1.1rem; line-height: 1.8;">
              <div style="margin-bottom: 1rem;"><strong style="font-size: 1.2rem;">Rod:</strong> <span style="font-size: 1.1rem;">${escapeHtml(warranty.rodSeries)} - ${escapeHtml(warranty.rodModel || 'N/A')}</span></div>
              <div style="margin-bottom: 1rem;"><strong style="font-size: 1.2rem;">Purchase Date:</strong> <span style="font-size: 1.1rem;">${warranty.purchaseDate ? formatDate(warranty.purchaseDate) : 'Not provided'}</span></div>
              <div><strong style="font-size: 1.2rem;">Warranty Status:</strong> <span style="color: ${fees.warrantyTier === 'Full' ? '#28a745' : fees.warrantyTier === 'Limited' ? '#ffc107' : '#dc3545'}; font-weight: 700; font-size: 1.2rem;">${fees.warrantyTier} Warranty</span></div>
            </div>
            
            <div style="border-top: 3px solid #ddd; padding-top: 2rem; margin-top: 2rem;">
              <div style="display: flex; justify-content: space-between; margin-bottom: 1.5rem; font-size: 1.2rem; padding: 0.5rem 0;">
                <span style="font-weight: 600;">Replacement Fee:</span>
                <span style="font-weight: 700; font-size: 1.3rem;">$${fees.replacementFee.toFixed(2)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 1.5rem; font-size: 1.2rem; padding: 0.5rem 0;">
                <span style="font-weight: 600;">Shipping Fee:</span>
                <span style="font-weight: 700; font-size: 1.3rem;">$${fees.shippingFee.toFixed(2)}</span>
              </div>
              <div style="border-top: 4px solid #000; padding-top: 1.5rem; margin-top: 1.5rem; display: flex; justify-content: space-between; font-size: 1.5rem; font-weight: 700; background: #fff; padding: 1.5rem; border-radius: 8px;">
                <span>TOTAL:</span>
                <span style="color: #0066cc; font-size: 1.8rem;">$${fees.totalFee.toFixed(2)}</span>
              </div>
            </div>
            
            ${fees.warrantyTier === 'Full' ? `
              <div style="background: #d4edda; padding: 1.5rem; border-radius: 8px; margin-top: 2rem; font-size: 1.1rem; color: #155724; border: 2px solid #c3e6cb; font-weight: 600;">
                ✓ Your rod is within the Full Warranty period. Replacement fee is waived with proof of purchase.
              </div>
            ` : fees.warrantyTier === 'Limited' ? `
              <div style="background: #fff3cd; padding: 1.5rem; border-radius: 8px; margin-top: 2rem; font-size: 1.1rem; color: #856404; border: 2px solid #ffeaa7; font-weight: 600;">
                ⚠ Your rod is in the Limited Warranty period. A replacement fee applies.
              </div>
            ` : `
              <div style="background: #f8d7da; padding: 1.5rem; border-radius: 8px; margin-top: 2rem; font-size: 1.1rem; color: #721c24; border: 2px solid #f5c6cb; font-weight: 600;">
                ⚠ Your warranty period has expired. We may still process your claim. Please contact us for more information.
              </div>
            `}
          </div>
        </div>
      `;
      showNext = true;
    } else if (step === 2) {
      // Step 2: Shipping address
      stepContent = `
        <div class="claim-step-content" style="padding: 2rem 0;">
          <h3 style="margin-bottom: 2rem; font-size: 1.8rem; font-weight: 700; color: #1a1a1a;">Shipping Address</h3>
          <p style="margin-bottom: 2.5rem; color: #555; font-size: 1.2rem; line-height: 1.6; font-weight: 500;">Please provide the address where you'd like the replacement rod shipped:</p>
          <form id="claimShippingForm">
            <div class="form-group" style="margin-bottom: 3rem !important;">
              <label class="form-label" style="margin-bottom: 1.5rem !important; font-size: 1.6rem !important; font-weight: 700 !important; display: block !important; color: #1a1a1a !important;">Street Address *</label>
              <input type="text" id="claim-address" class="claim-shipping-input" required value="${escapeHtml(currentClaim.shippingAddress.address || '')}" placeholder="NO P.O. BOX">
            </div>
            <div class="claim-form-row" style="display: grid !important; grid-template-columns: 2fr 1fr 1.2fr !important; gap: 2rem !important; margin-bottom: 3rem !important;">
              <div class="form-group" style="margin-bottom: 0 !important;">
                <label class="form-label" style="margin-bottom: 1.5rem !important; font-size: 1.6rem !important; font-weight: 700 !important; display: block !important; color: #1a1a1a !important;">City *</label>
                <input type="text" id="claim-city" class="claim-shipping-input" required value="${escapeHtml(currentClaim.shippingAddress.city || '')}" placeholder="City">
              </div>
              <div class="form-group" style="margin-bottom: 0 !important;">
                <label class="form-label" style="margin-bottom: 1.5rem !important; font-size: 1.6rem !important; font-weight: 700 !important; display: block !important; color: #1a1a1a !important;">State *</label>
                <input type="text" id="claim-state" class="claim-shipping-input" required value="${escapeHtml(currentClaim.shippingAddress.state || '')}" maxlength="2" placeholder="AL" style="text-transform: uppercase; text-align: center;">
              </div>
              <div class="form-group" style="margin-bottom: 0 !important;">
                <label class="form-label" style="margin-bottom: 1.5rem !important; font-size: 1.6rem !important; font-weight: 700 !important; display: block !important; color: #1a1a1a !important;">ZIP *</label>
                <input type="text" id="claim-zip" class="claim-shipping-input" required value="${escapeHtml(currentClaim.shippingAddress.zip || '')}" pattern="[0-9]{5}(-[0-9]{4})?" placeholder="12345">
              </div>
            </div>
            <div class="form-group" style="margin-bottom: 3rem !important;">
              <label class="form-label" style="margin-bottom: 1.5rem !important; font-size: 1.6rem !important; font-weight: 700 !important; display: block !important; color: #1a1a1a !important;">Phone Number</label>
              <input type="tel" id="claim-phone" class="claim-shipping-input" value="${escapeHtml(currentClaim.shippingAddress.phone || '')}" placeholder="Optional">
            </div>
          </form>
        </div>
      `;
      showNext = true;
      nextButtonText = 'Continue to Payment';
    } else if (step === 3) {
      // Step 3: Payment and submit
      stepContent = `
        <div class="claim-step-content" style="padding: 2rem 0;">
          <h3 style="margin-bottom: 2rem; font-size: 1.8rem; font-weight: 700; color: #1a1a1a;">Review & Submit</h3>
          <div style="background: #f8f9fa; padding: 3rem; border-radius: 12px; margin-bottom: 2.5rem; border: 2px solid #e0e0e0;">
            <h4 style="margin-top: 0; margin-bottom: 1.5rem; font-size: 1.4rem; font-weight: 700; color: #1a1a1a;">Shipping Address:</h4>
            <p style="margin: 0 0 2rem 0; font-size: 1.2rem; line-height: 1.8; color: #333;">
              ${escapeHtml(currentClaim.shippingAddress.address || '')}<br>
              ${escapeHtml(currentClaim.shippingAddress.city || '')}, ${escapeHtml(currentClaim.shippingAddress.state || '')} ${escapeHtml(currentClaim.shippingAddress.zip || '')}
            </p>
            <h4 style="margin-top: 2rem; margin-bottom: 1rem; font-size: 1.4rem; font-weight: 700; color: #1a1a1a;">Total Amount:</h4>
            <p style="font-size: 2rem; font-weight: 700; margin: 1rem 0; color: #0066cc;">$${fees.totalFee.toFixed(2)}</p>
          </div>
          <div style="background: #e8f4f8; padding: 2rem; border-radius: 12px; margin-bottom: 2rem; font-size: 1.1rem; border: 2px solid #b3e5fc; line-height: 1.8;">
            <strong style="font-size: 1.3rem; display: block; margin-bottom: 1.5rem; color: #0066cc;">Next Steps:</strong>
            <ol style="margin: 0; padding-left: 2rem; line-height: 2;">
              <li style="margin-bottom: 1rem;">Click "Submit Claim & Pay" to process payment</li>
              <li style="margin-bottom: 1rem;">Print your warranty form</li>
              <li style="margin-bottom: 1rem;">Cut out the 6" section of your rod blank</li>
              <li style="margin-bottom: 1rem;">Package rod section, warranty form, and receipt</li>
              <li style="margin-bottom: 1rem;">Ship to: <strong>Duckett Fishing, 250 Conner Island Pkwy, Guntersville, AL 35976</strong></li>
            </ol>
          </div>
        </div>
      `;
      showNext = true;
      nextButtonText = 'Submit Claim & Pay';
      showBack = true;
    }
    
    const modalHTML = `
      <style>
        /* COMPLETE OVERHAUL - New class name to avoid ALL conflicts */
        .claim-shipping-input {
          width: 100% !important;
          padding: 2.5rem 2rem !important;
          font-size: 1.6rem !important;
          line-height: 1.8 !important;
          min-height: 90px !important;
          height: 90px !important;
          max-height: 90px !important;
          background: #ffffff !important;
          color: #1a1a1a !important;
          -webkit-text-fill-color: #1a1a1a !important;
          border: 4px solid #ddd !important;
          border-radius: 12px !important;
          box-sizing: border-box !important;
          -webkit-appearance: none !important;
          appearance: none !important;
          display: block !important;
          margin: 0 !important;
          outline: none !important;
          font-family: inherit !important;
          transition: border-color 0.3s, box-shadow 0.3s !important;
        }
        .claim-shipping-input:focus {
          border-color: #0066cc !important;
          box-shadow: 0 0 0 4px rgba(0,102,204,0.15) !important;
          background: #ffffff !important;
        }
        .claim-shipping-input::placeholder {
          color: #999 !important;
          opacity: 1 !important;
          font-size: 1.6rem !important;
        }
        .claim-shipping-input::-webkit-input-placeholder {
          color: #999 !important;
          opacity: 1 !important;
          font-size: 1.6rem !important;
        }
        .claim-shipping-input::-moz-placeholder {
          color: #999 !important;
          opacity: 1 !important;
          font-size: 1.6rem !important;
        }
        .claim-shipping-input:-ms-input-placeholder {
          color: #999 !important;
          opacity: 1 !important;
          font-size: 1.6rem !important;
        }
        .claim-shipping-input:-moz-placeholder {
          color: #999 !important;
          opacity: 1 !important;
          font-size: 1.6rem !important;
        }
        .claim-form-row {
          display: grid !important;
          grid-template-columns: 2fr 1fr 1.2fr !important;
          gap: 2rem !important;
          margin-bottom: 3rem !important;
        }
        .claim-form-row .claim-shipping-input {
          width: 100% !important;
        }
        #claimModal input::placeholder {
          color: #999 !important;
          opacity: 1 !important;
        }
        #claimModal input::-webkit-input-placeholder {
          color: #999 !important;
          opacity: 1 !important;
        }
        #claimModal input::-moz-placeholder {
          color: #999 !important;
          opacity: 1 !important;
        }
        #claimModal input:-ms-input-placeholder {
          color: #999 !important;
          opacity: 1 !important;
        }
        #claimModal input:-moz-placeholder {
          color: #999 !important;
          opacity: 1 !important;
        }
      </style>
      <div class="modal-overlay active" id="claimModal" style="padding: 2rem; align-items: flex-start; padding-top: 3rem; background: rgba(0,0,0,0.6);">
        <div class="modal" style="max-width: 1000px; width: 95vw; max-height: 95vh; padding: 3rem; overflow-y: auto; font-size: 16px; line-height: 1.6; background: #ffffff; border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.3);">
          <div class="modal-header" style="margin-bottom: 2rem; padding-bottom: 1.5rem; border-bottom: 3px solid #0066cc;">
            <h2 class="modal-title" style="font-size: 2rem; margin: 0; font-weight: 700; color: #1a1a1a;">Start Warranty Claim</h2>
            <button class="modal-close" aria-label="Close" style="font-size: 2rem; padding: 0.5rem 1rem; line-height: 1; color: #666; background: transparent; border: none; cursor: pointer;">&times;</button>
          </div>
          
          <div style="margin-bottom: 3rem; display: flex; gap: 1.5rem; justify-content: center;">
            <div style="flex: 1; text-align: center; padding: 1rem 1.5rem; background: ${step >= 1 ? '#0066cc' : '#e0e0e0'}; color: ${step >= 1 ? 'white' : '#666'}; border-radius: 8px; font-size: 1.1rem; font-weight: ${step === 1 ? 'bold' : 'normal'}; transition: all 0.2s; min-height: 60px; display: flex; align-items: center; justify-content: center;">1. Fees</div>
            <div style="flex: 1; text-align: center; padding: 1rem 1.5rem; background: ${step >= 2 ? '#0066cc' : '#e0e0e0'}; color: ${step >= 2 ? 'white' : '#666'}; border-radius: 8px; font-size: 1.1rem; font-weight: ${step === 2 ? 'bold' : 'normal'}; transition: all 0.2s; min-height: 60px; display: flex; align-items: center; justify-content: center;">2. Address</div>
            <div style="flex: 1; text-align: center; padding: 1rem 1.5rem; background: ${step >= 3 ? '#0066cc' : '#e0e0e0'}; color: ${step >= 3 ? 'white' : '#666'}; border-radius: 8px; font-size: 1.1rem; font-weight: ${step === 3 ? 'bold' : 'normal'}; transition: all 0.2s; min-height: 60px; display: flex; align-items: center; justify-content: center;">3. Submit</div>
          </div>
          
          <div style="min-height: 400px; font-size: 18px;">
            ${stepContent}
          </div>
          
          <div class="form-actions" style="display: flex; gap: 1.5rem; justify-content: flex-end; margin-top: 3rem; padding-top: 2rem; border-top: 3px solid #e0e0e0;">
            ${showBack ? `<button type="button" class="btn btn-secondary btn-claim-back" data-step="${step - 1}" style="padding: 1.2rem 2.5rem; font-size: 1.1rem; min-width: 150px; font-weight: 600; border-radius: 6px; cursor: pointer;">← Back</button>` : ''}
            <button type="button" class="btn btn-secondary btn-cancel" style="padding: 1.2rem 2.5rem; font-size: 1.1rem; min-width: 150px; font-weight: 600; border-radius: 6px; cursor: pointer;">Cancel</button>
            ${showNext ? `<button type="button" class="btn btn-primary btn-claim-next" data-step="${step + 1}" style="padding: 1.2rem 3rem; font-size: 1.1rem; font-weight: 700; min-width: 220px; border-radius: 6px; cursor: pointer; background: #0066cc; color: white; border: none;">${nextButtonText} →</button>` : ''}
          </div>
        </div>
      </div>
    `;
    
    // Remove existing modal if any
    const existingModal = document.getElementById('claimModal');
    if (existingModal) existingModal.remove();
    
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    
    // Setup Cancel button handler for claim modal
    const cancelBtn = document.querySelector('#claimModal .btn-cancel');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        const claimModal = document.getElementById('claimModal');
        if (claimModal) {
          claimModal.remove();
          // Reset claim state
          currentClaim = null;
          currentClaimStep = 1;
        }
      });
    }
    
    // Setup modal close handler
    const modalCloseBtn = document.querySelector('#claimModal .modal-close');
    if (modalCloseBtn) {
      modalCloseBtn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        const claimModal = document.getElementById('claimModal');
        if (claimModal) {
          claimModal.remove();
          // Reset claim state
          currentClaim = null;
          currentClaimStep = 1;
        }
      });
    }
    
    // Setup form submission for step 2
    if (step === 2) {
      const form = document.getElementById('claimShippingForm');
      const nextBtn = document.querySelector('.btn-claim-next');
      if (form && nextBtn) {
        nextBtn.addEventListener('click', function() {
          if (!form.checkValidity()) {
            form.reportValidity();
            return;
          }
          // Save shipping address
          currentClaim.shippingAddress = {
            address: document.getElementById('claim-address').value,
            city: document.getElementById('claim-city').value,
            state: document.getElementById('claim-state').value,
            zip: document.getElementById('claim-zip').value,
            phone: document.getElementById('claim-phone').value
          };
          handleClaimStep(3);
        });
      }
    }
    
    // Setup final submission for step 3
    if (step === 3) {
      const submitBtn = document.querySelector('#claimModal .btn-claim-next');
      if (submitBtn) {
        // Mark this button so the delegated handler skips it
        submitBtn.setAttribute('data-submit-handler', 'true');
        
        // Clone to remove any existing listeners
        const newSubmitBtn = submitBtn.cloneNode(true);
        newSubmitBtn.setAttribute('data-submit-handler', 'true');
        submitBtn.parentNode.replaceChild(newSubmitBtn, submitBtn);
        
        // Add click handler with capture to run before delegated handler
        newSubmitBtn.addEventListener('click', async function(e) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          console.log('Submit claim button clicked (step 3)');
          
          // Validate shipping address
          if (!currentClaim.shippingAddress || !currentClaim.shippingAddress.address) {
            alert('Please complete the shipping address in step 2.');
            handleClaimStep(2);
            return;
          }
          
          // Disable button
          newSubmitBtn.disabled = true;
          const originalText = newSubmitBtn.textContent;
          newSubmitBtn.textContent = 'Submitting...';
          
          try {
            await submitWarrantyClaim();
            // If we get here, submission was successful - button text will be changed by submitWarrantyClaim
          } catch (error) {
            console.error('Error in submit handler:', error);
            // Re-enable button on error
            newSubmitBtn.disabled = false;
            newSubmitBtn.textContent = originalText;
            
            // Show error to user
            const errorModal = `
              <div class="modal-overlay active" style="z-index: 10001; padding: 2rem;">
                <div class="modal" style="max-width: 600px; padding: 2rem;">
                  <h2 style="color: #dc3545; margin-bottom: 1rem;">Error Submitting Claim</h2>
                  <p style="color: #333; margin-bottom: 1.5rem;">${escapeHtml(error.message)}</p>
                  <button onclick="this.closest('.modal-overlay').remove();" class="btn btn-primary" style="padding: 0.75rem 1.5rem;">Close</button>
                </div>
              </div>
            `;
            document.body.insertAdjacentHTML('beforeend', errorModal);
          }
        }, true); // Capture phase
      }
    }
  }

  /**
   * Submit warranty claim to backend
   */
  async function submitWarrantyClaim() {
    if (!currentClaim) return;
    
    const { warranty, fees, shippingAddress } = currentClaim;
    
    // Prepare claim data
    const claimData = {
      warranty_id: warranty.id,
      customer_id: customerId,
      customer_email: customerEmail,
      rod_series: warranty.rodSeries,
      rod_model: warranty.rodModel,
      serial_number: warranty.serialNumber || null,
      purchase_date: warranty.purchaseDate,
      order_id: warranty.orderId || null,
      order_number: warranty.orderNumber || null,
      warranty_tier: fees.warrantyTier,
      replacement_fee: fees.replacementFee,
      shipping_fee: fees.shippingFee,
      total_fee: fees.totalFee,
      shipping_address: shippingAddress,
      claim_status: 'pending_review',
      payment_status: 'pending',
      created_at: new Date().toISOString()
    };
    
    try {
      if (!CLAIMS_ENDPOINT) {
        console.error('CLAIMS_ENDPOINT is not set. Config:', {
          apiEndpoint: API_ENDPOINT,
          claimsEndpoint: window.warrantyManagerConfig?.claimsEndpoint,
          config: window.warrantyManagerConfig
        });
        throw new Error('Claims endpoint not configured. Please ensure claimsEndpoint is set in the page template.');
      }
      
      console.log('Submitting claim to:', CLAIMS_ENDPOINT);
      console.log('Claim data:', claimData);
      console.log('Customer ID:', customerId);
      console.log('Customer Email:', customerEmail);
      
      // Validate shipping address
      if (!shippingAddress || !shippingAddress.address || !shippingAddress.city || !shippingAddress.state || !shippingAddress.zip) {
        throw new Error('Complete shipping address is required. Please go back to step 2.');
      }
      
      const response = await fetch(CLAIMS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(claimData),
        credentials: 'omit' // Don't send cookies
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      const responseText = await response.text();
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.status} - ${responseText}`);
      }
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        // Clean up HTML tags if response contains them
        const cleanError = responseText.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        throw new Error(`Invalid response: ${cleanError.substring(0, 200)}`);
      }
      
      if (!result.success) {
        // Clean up HTML tags from error message
        let errorMsg = result.error || 'Failed to submit claim';
        errorMsg = errorMsg.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        throw new Error(errorMsg);
      }
      
      // Success - close claim modal properly
      const claimModalEl = document.getElementById('claimModal');
      if (claimModalEl) {
        claimModalEl.remove();
      }
      
      // Reset claim state
      currentClaim = null;
      currentClaimStep = 1;
      
      if (result.payment_required && result.total_fee > 0 && result.checkout_url) {
        // Payment required - redirect to checkout immediately
        showNotification('Redirecting to checkout to complete payment...', 'info');
        
        // Save claim ID to sessionStorage so we can track it after payment
        if (result.claim_id) {
          sessionStorage.setItem('pending_claim_id', result.claim_id);
        }
        
        // Small delay to show notification, then redirect
        setTimeout(() => {
          window.location.href = result.checkout_url;
        }, 1000);
      } else {
        // No payment required - show success and allow printing form
        showNotification('✓ Warranty claim submitted successfully!', 'success');
        printWarrantyForm(warranty.id);
      }
      
    } catch (error) {
      console.error('Error submitting warranty claim:', error);
      
      // Show styled error modal instead of tiny alert
      const errorModal = `
        <div class="modal-overlay active" id="errorModal" style="z-index: 10001; padding: 2rem;">
          <div class="modal" style="max-width: 900px; width: 95vw; padding: 3rem; font-size: 18px; line-height: 1.6;">
            <div class="modal-header" style="margin-bottom: 2rem; padding-bottom: 1.5rem; border-bottom: 4px solid #dc3545;">
              <h2 class="modal-title" style="font-size: 2rem; margin: 0; color: #dc3545; font-weight: 700;">⚠️ Error Submitting Claim</h2>
              <button class="modal-close" onclick="document.getElementById('errorModal').remove();" aria-label="Close" style="font-size: 2rem; padding: 0.5rem 1rem; line-height: 1; color: #666; background: transparent; border: none; cursor: pointer;">&times;</button>
            </div>
            <div style="padding: 2.5rem; background: #f8d7da; border: 3px solid #dc3545; border-radius: 12px; margin-bottom: 2rem;">
              <p style="margin: 0; font-size: 1.2rem; color: #721c24; line-height: 1.8; font-weight: 600;">
                <strong style="font-size: 1.3rem; display: block; margin-bottom: 1rem;">Error:</strong> 
                <span style="font-size: 1.1rem; word-break: break-word;">${escapeHtml(error.message)}</span>
              </p>
            </div>
            <p style="margin-bottom: 2rem; color: #555; font-size: 1.1rem; line-height: 1.6;">
              Please try again or contact support at <a href="mailto:warranty@duckettfishing.com" style="color: #0066cc; font-weight: 600; text-decoration: underline;">warranty@duckettfishing.com</a> if the problem persists.
            </p>
            <div style="display: flex; justify-content: flex-end; gap: 1.5rem;">
              <button onclick="document.getElementById('errorModal').remove(); const claimModal = document.getElementById('claimModal'); if (claimModal) claimModal.style.display = 'flex';" class="btn btn-primary" style="padding: 1.2rem 3rem; font-size: 1.2rem; min-width: 180px; font-weight: 700; border-radius: 8px; cursor: pointer; background: #0066cc; color: white; border: none;">Close</button>
            </div>
          </div>
        </div>
      `;
      
      const existingError = document.getElementById('errorModal');
      if (existingError) existingError.remove();
      
      document.body.insertAdjacentHTML('beforeend', errorModal);
      
      // Also close claim modal
      const claimModal = document.getElementById('claimModal');
      if (claimModal) {
        claimModal.style.display = 'none';
      }
    }
  }

  /**
   * Calculate warranty expiration date based on series and purchase date
   */
  function calculateExpirationDate() {
    const seriesSelect = document.getElementById('rodSeries');
    const purchaseDateInput = document.getElementById('purchaseDate');
    const expirationDateInput = document.getElementById('expirationDate');
    
    if (!seriesSelect || !purchaseDateInput || !expirationDateInput) return;
    
    const series = seriesSelect.value;
    const purchaseDate = purchaseDateInput.value;
    
    if (!series || !purchaseDate) {
      expirationDateInput.value = '';
      return;
    }
    
    const seriesData = WARRANTY_SERIES[series];
    if (!seriesData || seriesData.duration === 0) {
      expirationDateInput.value = '';
      return;
    }
    
    // Calculate expiration date (purchase date + warranty duration in years)
    const purchase = new Date(purchaseDate);
    const expiration = new Date(purchase);
    expiration.setFullYear(purchase.getFullYear() + seriesData.duration);
    
    // Format as YYYY-MM-DD
    const year = expiration.getFullYear();
    const month = String(expiration.getMonth() + 1).padStart(2, '0');
    const day = String(expiration.getDate()).padStart(2, '0');
    
    expirationDateInput.value = `${year}-${month}-${day}`;
  }

  /**
   * Get warranty tier (Full or Limited) based on purchase date
   */
  function getWarrantyTier(warranty) {
    if (!warranty.rodSeries || !warranty.purchaseDate) {
      return { tier: null, text: '' };
    }
    
    const seriesData = WARRANTY_SERIES[warranty.rodSeries];
    if (!seriesData || seriesData.duration === 0) {
      return { tier: null, text: '' };
    }
    
    const purchaseDate = new Date(warranty.purchaseDate);
    const now = new Date();
    const oneYearFromPurchase = new Date(purchaseDate);
    oneYearFromPurchase.setFullYear(purchaseDate.getFullYear() + 1);
    
    if (now < oneYearFromPurchase) {
      return { tier: 'full', text: 'Full Warranty (1st Year)' };
    } else {
      return { tier: 'limited', text: 'Limited Warranty' };
    }
  }

  /**
   * Generate unique ID
   */
  function generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  /**
   * Format date for display
   */
  function formatDate(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Show notification message
   */
  function showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#d4edda' : '#d1ecf1'};
      color: ${type === 'success' ? '#155724' : '#0c5460'};
      padding: 1rem 1.5rem;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      max-width: 400px;
      animation: slideIn 0.3s ease-out;
    `;
    notification.textContent = message;
    
    document.body.appendChild(notification);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => notification.remove(), 300);
    }, 5000);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
